import React, { useEffect, useState } from "react";
import Footer from "../../Footer";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  fetchAllCustomerProduct,
  fetchCustomerProductById,
} from "../../../service/ProductService";
import Topbar from "../Navigations/Topbar";
import { fetchCategory } from "../../../service/CategoryService";
import { useToast } from "../../toast/ToastProvider";
import { apiUrl } from "../../../config";
import { fetchProductImage } from "../../../service/ProductImageService";
import {
  createCart,
  getCartProductsByCustomerId,
} from "../../../service/CartService";
import { formatINR } from "../../../App";
import { useSelector } from "react-redux";
// Import wishlist services
import {
  createWishlist,
  deleteWishlistById,
  getWishlistProductsByCustomerId,
} from "../../../service/WishlistService";
import "../../../styles/filters.css";

const QuickViewModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div
      className="quick-view-modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
      }}
    >
      <div
        className="quick-view-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "8px",
          maxWidth: "80%",
          maxHeight: "80%",
          overflowY: "auto",
          position: "relative",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "none",
            border: "none",
            fontSize: "24px",
            cursor: "pointer",
            zIndex: 1001,
          }}
        >
          &times;
        </button>
        {children}
      </div>
    </div>
  );
};

const ProductDetails = () => {
  const { id } = useParams();
  const toast = useToast();
  const [product, setProduct] = useState({});
  const [customerId, setCustomerId] = useState(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [activeImage, setActiveImage] = useState("");
  const authUser = useSelector((state) => state.auth.authUser);
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]); // Added wishlist state
  const navigate = useNavigate();

  const isProductInCart = (productId, customerId) => {
    return cart.some(
      (item) => item.productId === productId && item.customerId === customerId,
    );
  };

  const isProductInWishlist = (productId) => {
    return wishlist.some((item) => item.productId === productId);
  };

  const productExistInCart = isProductInCart(product?.id, authUser?.id);
  const productInWishlist = isProductInWishlist(product?.id);

  const fetchProduct = async (id) => {
    try {
      const productData = await fetchCustomerProductById(id);
      const images = await fetchProductImage();

      const productImages = images.filter(
        (img) => img.productId === productData.id,
      );
      setProduct({ ...productData, images: productImages });

      if (productImages.length > 0) {
        setActiveImage(`${apiUrl}/${productImages[0].imageUrl}`);
      } else {
        setActiveImage("");
      }
    } catch (error) {
      console.error("Failed to fetch product:", error);
      toast("Failed to fetch product", "ERROR");
    }
  };

  const fetchData = async () => {
    try {
      await fetchAllCustomerProduct();
      await fetchCategory();
      if (authUser?.id) {
        setCustomerId(authUser.id);
      }
    } catch (error) {
      console.error("Error in fetchData:", error);
    }
  };
  const fetchWishlistData = async () => {
    if (!authUser?.id) return;
    try {
      const response = await getWishlistProductsByCustomerId(authUser.id);
      setWishlist(response || []);
    } catch (error) {
      console.error("Failed to fetch wishlist:", error);
      toast("Failed to load wishlist", "ERROR");
    }
  };

  const fetchCartData = async () => {
    if (!authUser?.id) return;
    try {
      const cartData = await getCartProductsByCustomerId(authUser.id);
      setCart(cartData);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
      toast("Failed to load cart", "ERROR");
    }
  };

  const toggleWishlist = async (productId) => {
    if (authUser === null) {
      navigate("/login");
      return;
    }

    try {
      if (productInWishlist) {
        const wishlistItem = wishlist.find(
          (item) => item.productId === productId,
        );
        if (wishlistItem) {
          await deleteWishlistById(wishlistItem.wishlistId);
          setWishlist((prev) =>
            prev.filter((item) => item.productId !== productId),
          );
          toast("Removed from wishlist", "SUCCESS");
        }
      } else {
        await createWishlist({ customerId: authUser.id, productId });
        const updatedWishlist = await getWishlistProductsByCustomerId(
          authUser.id,
        );
        setWishlist(updatedWishlist);
        toast("Added to wishlist", "SUCCESS");
      }
    } catch (error) {
      console.error("Wishlist error:", error);
      toast("Failed to update wishlist", "ERROR");
    }
  };

  const addToCart = async (productId) => {
    if (productExistInCart) {
      navigate("/shopping-cart");
      return;
    }

    if (authUser === null) {
      navigate("/login");
      return;
    }

    if (!productId) {
      toast("Invalid product", "ERROR");
      return;
    }

    try {
      await createCart({ customerId: authUser.id, productId, quantity: 1 });
      toast("Product added to cart", "SUCCESS");
      fetchCartData();
    } catch (error) {
      console.error("Add to cart error:", error);
      toast("Failed to add product", "ERROR");
    }
  };

  const handleQuickView = () => {
    setIsQuickViewOpen(true);
  };

  const handleCloseQuickView = () => {
    setIsQuickViewOpen(false);
  };

  useEffect(() => {
    if (id) {
      fetchProduct(id);
    }
    fetchData();
  }, [id]);

  useEffect(() => {
    fetchCartData();
    fetchWishlistData();
  }, [authUser?.id]);

  const productPrice = product.ornamentPrice === "Out of Stock";
  const isFrontendOutOfStock =
    product.stockAvailability?.toLowerCase() !== "yes";
  const isOutOfStock = productPrice || isFrontendOutOfStock;
  const productDiscount = product.discount;
  const productBeforeGst = product.priceBeforeGst;
  const discountPrice = Math.round(
    productDiscount > 0
      ? product.ornamentPrice - (productBeforeGst * productDiscount) / 100
      : product.ornamentPrice,
  );
  const hasDiscount = product.discount > 0;

  const getPurityValue = (purity) => {
    if (purity === "22k") return "91.6";
    if (purity === "24k") return "999.9";
    return purity;
  };
  return (
    <div className="details-page">
      <header className="header style7">
        <Topbar />
      </header>
      <div className="main-content main-content-details single left-sidebar">
        <div className="container">
          <div className="row pt-4 pb-2">
            <div className="col-lg-12">
              <div className="breadcrumb-trail breadcrumbs">
                <ul className="trail-items breadcrumb">
                  <li className="trail-item trail-begin">
                    <Link to="/" className="text-decoration-none">
                      Home
                    </Link>
                  </li>
                  <li className="trail-item trail-end active">
                    <Link to="/products" className="text-decoration-none">
                      Products
                    </Link>
                  </li>
                  <li className="trail-item trail-end active">
                    Product Details
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="row mt-3">
            <div className=" col-lg-4 col-md-6 col-sm-12 details-thumd">
              <div
                className="image-preview-container image-thick-box image_preview_container"
                style={{
                  height: "345px",
                  width: "330px",
                  position: "relative",
                }}
              >
                {hasDiscount && !isOutOfStock && (
                  <span
                    style={{
                      position: "absolute",
                      top: "0",
                      left: "0",
                      background: "#c05c2e",
                      color: "#fff",
                      padding: "5px 18px",
                      fontSize: "13px",
                      fontWeight: "600",
                      borderRadius: "0 0 14px 0",
                      zIndex: 1,
                    }}
                  >
                    {product.discount}% Discount
                  </span>
                )}
                {activeImage ? (
                  <img
                    id="img_zoom"
                    data-zoom-image={activeImage}
                    src={activeImage}
                    alt={product.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f8f9fa",
                      color: "#6c757d",
                    }}
                  >
                    No Image Available
                  </div>
                )}
              </div>

              {/* Thumbnail Strip */}
              {product?.images?.length > 0 && (
                <div className="product-preview image-small product_preview">
                  <div
                    className="d-flex gap-2 mt-2 p-2"
                    style={{
                      overflowX: "auto",
                      whiteSpace: "nowrap",
                      scrollbarWidth: "thin",
                      padding: "8px 0",
                      maxHeight: "90px",
                    }}
                  >
                    {product.images.map((img, index) => {
                      const imgUrl = `${apiUrl}/${img.imageUrl}`;
                      return (
                        <div
                          key={index}
                          style={{
                            border:
                              activeImage === imgUrl
                                ? "2px solid #007bff"
                                : "2px solid transparent",
                            borderRadius: "4px",
                            cursor: "pointer",
                            minWidth: "60px",
                            transition: "border 0.2s ease",
                          }}
                          onClick={() => setActiveImage(imgUrl)}
                          title={`View image ${index + 1}`}
                        >
                          <img
                            src={imgUrl}
                            alt={`thumbnail ${index}`}
                            style={{
                              width: "60px",
                              height: "60px",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="col-lg-4 col-md-6 col-sm-12 ms-0 details-infor">
              <h1
                className="product-title"
                style={{ textTransform: "capitalize" }}
              >
                {product.name}
              </h1>
              <div className="stars-rating">
                <div className="star-rating">
                  <span className="star-5"></span>
                </div>
                <div className="count-star">(7)</div>
              </div>
              <div className="price">
                <span>
                  {!isOutOfStock && (
                    <div className="price">
                      {productDiscount > 0 ? (
                        <>
                          <p>{formatINR(discountPrice)}</p>
                          <span
                            style={{
                              textDecoration: "line-through",
                              color: "#888",
                            }}
                          >
                            {formatINR(Math.round(product.ornamentPrice))}
                          </span>
                        </>
                      ) : (
                        <span style={{ color: "black" }}>
                          {formatINR(Math.round(product.ornamentPrice))}
                        </span>
                      )}
                    </div>
                  )}
                </span>
              </div>
              <div className="price">
                <span>{product.weight}g</span>
              </div>
              <div className="group-button">
                <div className="yith-wcwl-add-to-wishlist">
                  <div className="yith-wcwl-add-button">
                    {!isOutOfStock && (
                      <button
                        onClick={() => {
                          toggleWishlist(product.id);
                        }}
                        className="text-decoration-none"
                        style={{
                          background: "none",
                          border: "none",
                          color: productInWishlist ? "red" : "inherit",
                          cursor: "pointer",
                          fontSize: "16px",
                          marginLeft: -20,
                        }}
                      >
                        <i
                          className={`fa ${
                            productInWishlist
                              ? "ph ph-heart-fill"
                              : "ph ph-heart"
                          }`}
                          style={{
                            color: productInWishlist ? "red" : "gray",
                          }}
                        ></i>
                        {productInWishlist
                          ? " Remove from Wishlist"
                          : " Add to Wishlist"}
                      </button>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-4 mt-3">
                  {isOutOfStock ? (
                    <p
                      style={{
                        marginTop: "5px",
                        fontSize: "15px",
                        fontWeight: 600,
                        color: "red",
                      }}
                    >
                      Out of Stock
                    </p>
                  ) : (
                    <button
                      className={
                        productExistInCart
                          ? "go-cart-btn py-3 px-5 fs-5"
                          : "add-cart-btn py-3 px-5 fs-5"
                      }
                      onClick={() => addToCart(product.id)}
                      disabled={!product.id}
                    >
                      {productExistInCart ? "Go To Cart" : "Add To Cart"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-12">
              <h5
                style={{
                  fontWeight: "550",
                  marginBottom: "10px",
                  fontSize: "18px",
                  marginTop: "10px",
                }}
              >
                Product Description
              </h5>

              <p
                style={{
                  color: "#555",
                  lineHeight: "1.7",
                  fontSize: "15px",
                  textAlign: "justify",
                  textIndent: "40px",
                }}
              >
                {product?.description
                  ? product.description.charAt(0).toUpperCase() +
                    product.description.slice(1)
                  : ""}
              </p>
            </div>
            <div className="tab-details-product mt-3">
              <ul className="tab-link">
                {/* <li className="active">
                      <Link
                        data-toggle="tab"
                        aria-expanded="true"
                        to="#product-descriptions"
                        className="text-decoration-none"
                      >
                        Descriptions
                      </Link>
                    </li> */}
                <li className="">
                  <Link
                    data-toggle="tab"
                    aria-expanded="true"
                    to="#information"
                    className="text-decoration-none"
                  >
                    Price Breakup
                  </Link>
                </li>
                <li className="">
                  <Link
                    data-toggle="tab"
                    aria-expanded="true"
                    to="#reviews"
                    className="text-decoration-none"
                  >
                    Reviews
                  </Link>
                </li>
              </ul>
              <div className="tab-container">
                {!isOutOfStock && (
                  <div
                    id="information"
                    className="tab-panel active price-breakup-table-wrapper"
                    style={{
                      border: "none",
                      borderRadius: "8px",
                      overflow: "hidden",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                      margin: "5px",
                      maxWidth: "700px",
                      width: "100%",
                    }}
                  >
                    <h5
                      style={{
                        color: "black",
                        padding: "12px",
                        margin: 0,
                        fontWeight: "600",
                        fontSize: "16px",
                      }}
                    >
                      Price Breakup
                    </h5>

                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "14px",
                        border: "none",
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            borderBottom: "2px solid black",
                            textAlign: "left",
                            background: "#fff",
                          }}
                        >
                          <th
                            style={{
                              padding: "10px",
                              color: "black",
                              fontWeight: "550",
                              fontSize: "15px",
                            }}
                          >
                            Component
                          </th>
                          <th
                            style={{
                              padding: "10px",
                              color: "black",
                              fontWeight: "550",
                              fontSize: "15px",
                            }}
                          >
                            Rate
                          </th>
                          <th
                            style={{
                              padding: "10px",
                              color: "black",
                              fontWeight: "550",
                              fontSize: "15px",
                            }}
                          >
                            Weight
                          </th>
                          <th
                            style={{
                              padding: "10px",
                              color: "black",
                              fontWeight: "550",
                              fontSize: "15px",
                            }}
                          >
                            Final Value
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {/* Gold */}
                        <tr className="price-row">
                          <td
                            style={{
                              padding: "10px",
                              color: "black",
                              fontWeight: "600",
                            }}
                          >
                            Gold ({getPurityValue(product.purity)})
                          </td>
                          <td style={{ color: "black" }}>
                            {formatINR(product.goldRate)}
                          </td>
                          <td style={{ color: "black" }}>{product.weight} g</td>
                          <td style={{ color: "black" }}>
                            {formatINR(Math.round(product.baseValue))}
                          </td>
                        </tr>

                        {/* Making */}
                        <tr className="price-row">
                          <td style={{ padding: "10px", color: "black" }}>
                            Making Charges ({product.makingCharge}%)
                          </td>
                          <td></td>
                          <td>-</td>
                          <td style={{ color: "black" }}>
                            {formatINR(Math.round(product.makingAmount))}
                          </td>
                        </tr>

                        {/* Wastage */}
                        <tr className="price-row">
                          <td
                            colSpan="3"
                            style={{
                              padding: "10px",
                              color: "black",
                            }}
                          >
                            Wastage ({product.wastageCharge}%)
                          </td>
                          <td style={{ color: "black" }}>
                            {formatINR(Math.round(product.wastageAmount))}
                          </td>
                        </tr>

                        {/* Subtotal */}
                        <tr style={{ fontWeight: "600" }}>
                          <td
                            colSpan="3"
                            style={{
                              padding: "10px",
                              color: "black",
                              fontWeight: "600",
                            }}
                          >
                            Total
                          </td>
                          <td style={{ color: "black", fontWeight: "600" }}>
                            {formatINR(Math.round(product.priceBeforeGst))}
                          </td>
                        </tr>
                        {/* Discount */}
                        {product.discount > 0 && (
                          <tr
                            className="price-row"
                            style={{
                              borderTop: "1px solid black",
                              fontWeight: "700",
                              fontSize: "15px",
                            }}
                          >
                            <td
                              colSpan="3"
                              style={{
                                padding: "10px",
                                color: "black",
                              }}
                            >
                              Discount ({product.discount}%)
                            </td>
                            <td style={{ color: "black" }}>
                              {formatINR(
                                Math.round(
                                  (product.priceBeforeGst * product.discount) /
                                    100,
                                ),
                              )}
                            </td>
                          </tr>
                        )}

                        {/* GST */}
                        {product.gst > 0 && (
                          <tr className="price-row">
                            <td
                              colSpan="3"
                              style={{ padding: "10px", color: "black" }}
                            >
                              GST ({product.gst}%)
                            </td>
                            <td style={{ color: "black" }}>
                              {formatINR(Math.round(product.gstAmount))}
                            </td>
                          </tr>
                        )}

                        {/* Grand Total */}
                        <tr
                          style={{
                            borderTop: "2px solid black",
                            fontWeight: "700",
                            fontSize: "15px",
                          }}
                        >
                          <td
                            colSpan="3"
                            style={{
                              padding: "12px",
                              color: "black",
                              fontWeight: "600",
                            }}
                          >
                            Grand Total
                          </td>
                          <td style={{ color: "black", fontWeight: "600" }}>
                            {formatINR(discountPrice)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                <div id="reviews" className="tab-panel">
                  <div className="reviews-tab">
                    <div className="comments">
                      <h2 className="reviews-title">
                        1 review for
                        <span>Splendid Diamond</span>
                      </h2>
                      <ol className="commentlist">
                        <li className="conment">
                          <div className="conment-container">
                            <Link to="#" className="avatar">
                              <img src="assets/images/avartar.jpeg" alt="img" />
                            </Link>
                            <div className="comment-text">
                              <div className="stars-rating">
                                <div className="star-rating">
                                  <span className="star-5"></span>
                                </div>
                                <div className="count-star">(1)</div>
                              </div>
                              <p className="meta">
                                <strong className="author">Cobus Bester</strong>
                                <span>-</span>
                                <span className="time">June 7, 2013</span>
                              </p>
                              <div className="description">
                                <p>
                                  Simple and effective design. One of my
                                  favorites.
                                </p>
                              </div>
                            </div>
                          </div>
                        </li>
                      </ol>
                    </div>
                    <div className="review_form_wrapper">
                      <div className="review_form">
                        <div className="comment-respond">
                          <span className="comment-reply-title">
                            Add a review
                          </span>
                          <form className="comment-form-review">
                            <p className="comment-notes">
                              <span className="email-notes">
                                Your email address will not be published.
                              </span>
                              Required fields are marked
                              <span className="required">*</span>
                            </p>
                            <div className="comment-form-rating">
                              <label>Your rating</label>
                              <p className="stars">
                                <span>
                                  <Link className="star-1" to="#"></Link>
                                  <Link className="star-2" to="#"></Link>
                                  <Link className="star-3" to="#"></Link>
                                  <Link className="star-4" to="#"></Link>
                                  <Link className="star-5" to="#"></Link>
                                </span>
                              </p>
                            </div>
                            <p className="comment-form-comment">
                              <label>
                                Your review
                                <span className="required">*</span>
                              </label>
                              <textarea
                                title="review"
                                id="comment"
                                name="comment"
                                cols="45"
                                rows="8"
                              ></textarea>
                            </p>
                            <p className="comment-form-author">
                              <label>
                                Name
                                <span className="">*</span>
                              </label>
                              <input
                                title="author"
                                id="author"
                                name="author"
                                type="text"
                                value=""
                              />
                            </p>
                            <p className="comment-form-email">
                              <label>
                                Email
                                <span className="">*</span>
                              </label>
                              <input
                                title="email"
                                id="email"
                                name="email"
                                type="email"
                                value=""
                              />
                            </p>
                            <p className="form-submit">
                              <input
                                name="submit"
                                type="submit"
                                id="submit"
                                className="submit"
                                value="Submit"
                              />
                            </p>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick View Modal */}
      <QuickViewModal isOpen={isQuickViewOpen} onClose={handleCloseQuickView}>
        <div className="kt-popup-quickview">
          <div className="details-thumb">
            <div className="slider-product slider-for">
              <div className="details-item">
                <img
                  src={
                    activeImage || "assets/images/popup-quickview-item-1.jpg"
                  }
                  alt="img"
                />
              </div>
            </div>
            <div className="slider-product-button slider-nav nav-center d-flex">
              {product?.images?.slice(0, 4).map((img, index) => (
                <div key={index} className="details-item">
                  <img
                    src={`${apiUrl}/${img.imageUrl}`}
                    alt={`quickview ${index}`}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="details-infor">
            <h1 className="product-title">{product.name}</h1>
            <div className="stars-rating">
              <div className="star-rating">
                <span className="star-5"></span>
              </div>
              <div className="count-star">(7)</div>
            </div>
            <div className="price">
              <span>
                {isNaN(product.ornamentPrice)
                  ? product.ornamentPrice
                  : formatINR(product.ornamentPrice)}
              </span>
            </div>
            <div className="price">
              <span>{product.weight}g</span>
            </div>
            <div className="group-button">
              <div className="yith-wcwl-add-to-wishlist">
                <div className="yith-wcwl-add-button">
                  <button
                    onClick={() => {
                      toggleWishlist(product.id);
                      handleCloseQuickView();
                    }}
                    className="text-decoration-none"
                  >
                    <i
                      className={`fa ${
                        productInWishlist ? "fa-heart" : "fa-heart-o"
                      }`}
                    ></i>
                    {productInWishlist
                      ? " Remove from Wishlist"
                      : " Add to Wishlist"}
                  </button>
                </div>
              </div>
              <div className="d-flex align-items-center gap-4 mt-3">
                <button
                  className={
                    productExistInCart
                      ? "btn btn-success py-2 px-4"
                      : "btn btn-primary py-2 px-4"
                  }
                  onClick={() => {
                    addToCart(product.id);
                    handleCloseQuickView();
                  }}
                >
                  {productExistInCart ? "Go To Cart" : "Add To Cart"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </QuickViewModal>

      <Footer />
      <div className="footer-device-mobile">
        <div className="wapper">
          <div className="footer-device-mobile-item device-home">
            <Link to="/" className="text-decoration-none">
              <span className="icon">
                <i className="ph ph-house"></i>
              </span>{" "}
              Home
            </Link>
          </div>
          <div className="footer-device-mobile-item device-home device-cart">
            <Link to="/wishlist" className="text-decoration-none">
              <span className="icon">
                <i className="ph ph-heart"></i>
                <span className="count-icon">{wishlist.length}</span>
              </span>{" "}
              Wishlist
            </Link>
          </div>
          <div className="footer-device-mobile-item device-home device-cart">
            <Link to="/shopping-cart" className="text-decoration-none">
              <span className="icon">
                <i className="ph ph-shopping-cart-simple" />
                <span className="count-icon">{cart.length}</span>
              </span>{" "}
              Cart
            </Link>
          </div>
          <div className="footer-device-mobile-item device-home device-user">
            <Link to="/profile" className="text-decoration-none">
              <span className="icon">
                <i className="ph ph-user"></i>
              </span>{" "}
              Account
            </Link>
          </div>
        </div>
      </div>
      <Link to="#" className="backtotop">
        <i
          className="ph ph-caret-double-up"
          style={{
            color: "white",
            fontSize: "30px",
          }}
        ></i>
      </Link>
    </div>
  );
};

export default ProductDetails;
